import { defineStore } from 'pinia'
import { reactive } from 'vue'

interface IStorePromises {
	[key: string]: Promise<any> | null
}

export interface ILocationURLSlugs {
	[key: string]: ICMSLocationModel | null
}

export interface IAssignedLocations {
	[key: string]: ICMSLocationRelatedModel[] | null
}

export interface IClosestLocations {
	[key: string]: ICMSLocationModel[] | null
}

export interface ICMSLocationContentSections {
	[key: string]: ICMSLocationContentModel[] | null
}

export interface IRentalLocationPagesState {
	cmsLocations: ILocationURLSlugs | null
	assignedLocations: IAssignedLocations | null
	closestLocations: IClosestLocations | null
	cmsLocationContentSections: ICMSLocationContentSections | null
}

export interface IRentalLocationPagesStore {
	readonly state: {
		cmsLocations: ILocationURLSlugs | null
		assignedLocations: IAssignedLocations | null
		closestLocations: IClosestLocations | null
		cmsLocationContentSections: ICMSLocationContentSections | null
	}
	fetchCmsLocationBySlug: (locationURLSlug: string, locale: string) => Promise<ICMSLocationModel | null>
	fetchAssignedLocations: (locationID: number) => Promise<ICMSLocationRelatedModel[] | null>
	fetchClosestLocations: (locationID: number) => Promise<ICMSLocationModel[] | null>
	fetchCMSLocationContentSections: (cmsLocationID: number, locale: string) => Promise<ICMSLocationContentModel[] | null>
}

export const useRentalLocationPagesStore = defineStore('rentalLocationPagesStore', (): IRentalLocationPagesStore => {
	const _state = reactive<IRentalLocationPagesState>({
		cmsLocations: null,
		assignedLocations: null,
		closestLocations: null,
		cmsLocationContentSections: null,
	})

	const locationLangSlug = (location: string, locale: string) => {
		return location + '_' + locale
	}

	const locationAssignedLocationsSlug = (locationID: number) => {
		return `assignedLocations_${locationID}`
	}

	const locationClosestLocationsSlug = (locationID: number) => {
		return `closestLocations_${locationID}`
	}

	const promises: IStorePromises = {}

	const fetchCmsLocationBySlug = async (locationURLSlug: string, locale: string) => {
		if (_state.cmsLocations && _state.cmsLocations[locationLangSlug(locationURLSlug, locale)]) {
			return _state.cmsLocations[locationLangSlug(locationURLSlug, locale)]
		}

		if (promises?.[locationLangSlug(locationURLSlug, locale)]) {
			return promises[locationLangSlug(locationURLSlug, locale)]
		}

		promises[locationLangSlug(locationURLSlug, locale)] = (async () => {
			const { data } = await ApiService.fetchCMSLocationBySlug(locationURLSlug, locale, true)
			if (data) {
				_state.cmsLocations = { ..._state.cmsLocations, [locationLangSlug(locationURLSlug, locale)]: data }
			} else {
				_state.cmsLocations = null
			}

			return _state.cmsLocations ? _state.cmsLocations[locationLangSlug(locationURLSlug, locale)] : null
		})()

		return await promises[locationLangSlug(locationURLSlug, locale)]
	}

	const fetchAssignedLocations = async (locationID: number) => {
		if (_state.assignedLocations && _state.assignedLocations[locationAssignedLocationsSlug(locationID)]) {
			return _state.assignedLocations[locationAssignedLocationsSlug(locationID)]
		}

		if (promises[locationAssignedLocationsSlug(locationID)]) {
			return promises[locationAssignedLocationsSlug(locationID)]
		}

		promises[locationAssignedLocationsSlug(locationID)] = (async () => {
			const { data } = await ApiService.fetchAssignedLocations(locationID)

			if (data && data.length > 0) {
				_state.assignedLocations = { ..._state.assignedLocations, [locationAssignedLocationsSlug(locationID)]: data }
			} else {
				_state.assignedLocations = null
			}

			return _state.assignedLocations ? _state.assignedLocations[locationAssignedLocationsSlug(locationID)] : null
		})()

		return await promises[locationAssignedLocationsSlug(locationID)]
	}

	const fetchClosestLocations = async (locationID: number) => {
		if (_state.closestLocations && _state.closestLocations[locationClosestLocationsSlug(locationID)]) {
			return _state.closestLocations[locationClosestLocationsSlug(locationID)]
		}

		if (promises[locationClosestLocationsSlug(locationID)]) {
			return promises[locationClosestLocationsSlug(locationID)]
		}

		promises[locationClosestLocationsSlug(locationID)] = (async () => {
			const { data } = await ApiService.fetchClosestLocations(locationID)

			if (data && data.length > 0) {
				_state.closestLocations = { ..._state.closestLocations, [locationClosestLocationsSlug(locationID)]: data }
			} else {
				_state.closestLocations = null
			}

			return _state.closestLocations ? _state.closestLocations[locationClosestLocationsSlug(locationID)] : null
		})()

		return await promises[locationClosestLocationsSlug(locationID)]
	}

	// const response = await ApiService.fetchCMSLocationContentSections(cmsLocation.value?.id, locale.value)

	const fetchCMSLocationContentSections = async (cmsLocationID: number, locale: string) => {
		const stringCmsLocationID = String(cmsLocationID)
		if (_state.cmsLocationContentSections && _state.cmsLocationContentSections[locationLangSlug(stringCmsLocationID, locale)]) {
			return _state.cmsLocationContentSections[locationLangSlug(stringCmsLocationID, locale)]
		}

		if (promises[locationLangSlug(stringCmsLocationID, locale)]) {
			return promises[locationLangSlug(stringCmsLocationID, locale)]
		}

		promises[locationLangSlug(stringCmsLocationID, locale)] = (async () => {
			const { data } = await ApiService.fetchCMSLocationContentSections(cmsLocationID, locale)

			if (data && data.length > 0) {
				_state.cmsLocationContentSections = { ..._state.cmsLocationContentSections, [locationLangSlug(stringCmsLocationID, locale)]: data }
			} else {
				_state.cmsLocationContentSections = null
			}

			return _state.cmsLocationContentSections ? _state.cmsLocationContentSections[locationLangSlug(stringCmsLocationID, locale)] : null
		})()

		return await promises[locationLangSlug(stringCmsLocationID, locale)]
	}

	return {
		state: _state,
		fetchCmsLocationBySlug,
		fetchAssignedLocations,
		fetchClosestLocations,
		fetchCMSLocationContentSections,
	}
})
